import { useEffect, useMemo, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  X,
  MapPin,
  Tag,
  Gauge,
  User,
  Calendar,
  CheckCircle2,
  Flag,
  Layers,
  Eye,
  Bell,
  Siren,
  Clock,
  Image as ImageIcon,
  RotateCcw,
  ChevronDown,
  Loader2,
} from "lucide-react";

import { cn } from "@/ui/lib/utils";
import { formatDate, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/components/ui/popover";
import { Button } from "@/ui/components/ui/button";
import { Checkbox } from "@/ui/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";

import { useGetClassificationTaxonomy, useGetOrganizationMembers } from "../../api";
import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore";
import { ALERT_TYPES, alertTypeLabel } from "@/alerts/types/machine-alert";
import type {
  AlertStatusFilter,
  ReviewFilters,
  ReviewPresence,
} from "../../types/review";
import type { TLocation } from "@/locations/types/location";
import type { TaxonomyFamily, ClassificationTaxonomy } from "../../types/taxonomy";
import ReviewPresetsMenu from "./review-presets-menu";
import type { ReviewFilterSnapshot, SavedPreset } from "../../utils/filter-presets";

/**
 * ──────────────────────────────────────────────────────────────────
 * Grouped filter bar
 * ──────────────────────────────────────────────────────────────────
 *
 * Filters are grouped by the data hierarchy they describe:
 *
 *   Image   → label, validity, confidence
 *   Event   → event state, alert status, alert type, event time
 *   Review  → presence, review state, reviewer, reviewed time
 *
 * Core principle, unchanged from the per-chip bar it replaces: NO
 * control mutates appliedFilters on every keystroke / click / tick.
 * A group popover holds a local DRAFT of exactly the request keys it
 * owns and commits the diff ONCE when it closes — so editing four
 * things inside one group is still a single backend request.
 *
 * Scope keeps its own chip and its own explicit Apply button: it is
 * the one action that throws away the dataset and the selection.
 *
 * What is applied reads back out as removable chips next to the group
 * buttons, and as a per-group count badge on the buttons themselves.
 *
 * `ordering` is deliberately NOT owned by any group: sorting is a
 * separate control on the page, and no filter edit may change it.
 * ────────────────────────────────────────────────────────────────── */

/** Identity of a refinement for clear-one-filter purposes. */
export type RevealedFilterKey =
  | "label"
  | "state"
  | "event_state"
  | "presence"
  | "confidence"
  | "reviewer"
  | "reviewed_in"
  | "validity"
  | "alert"
  | "alert_type"
  | "event_time";

/** The filter object the bar reads; the window is owned by the page. */
type AppliedReviewFilters = Omit<ReviewFilters, "start_time" | "end_time">;

/** A commit — only the keys that actually changed. */
type FilterPatch = Partial<ReviewFilters>;

type FilterField = keyof AppliedReviewFilters;

/** Per-family filter: member index, or "any" = all members of the family. */
type FamilyMemberFilter = number | "any";

export interface ReviewFilterBarProps {
  appliedFilters: AppliedReviewFilters;
  onApplyFilters: (patch: Partial<ReviewFilters>) => void;
  onClearFilter: (key: RevealedFilterKey) => void;
  onClearAll: () => void;
  /** Whether anything is refined at all — owned by useReviewFilters. */
  hasRefinement: boolean;
  // Scope
  locations: TLocation[];
  selectedLocationId: number | undefined;
  selectedMachineIds: Set<number>;
  onApplyScope: (locationId: number | undefined, machineIds: Set<number>) => void;
  hasScopeSelected: boolean;
  // Presets + history
  presets: SavedPreset[];
  history: ReviewFilterSnapshot[];
  onSavePreset: (name: string) => void;
  onDeletePreset: (id: string) => void;
  onApplySnapshot: (snapshot: ReviewFilterSnapshot) => void;
}

/* ──────────────────────────────────────────────────────────────────
 * CSV helpers — every `_in` filter is an OR-combined comma list
 * ────────────────────────────────────────────────────────────────── */

const csvList = (value: string | undefined): string[] =>
  value?.split(",").map((s) => s.trim()).filter(Boolean) ?? [];

const csvValue = (values: Iterable<string>): string | undefined => {
  const list = [...values];
  return list.length > 0 ? list.join(",") : undefined;
};

interface SelectOption {
  value: string;
  label: string;
}

const optionLabel = (options: readonly SelectOption[], value: string): string =>
  options.find((o) => o.value === value)?.label ?? value;

type PluralKey =
  | "filterBar.plural.states"
  | "filterBar.plural.verdicts"
  | "filterBar.plural.types";

/** "Resolved" for one pick, "2 verdicts" for several, null for none. */
const multiSummary = (
  value: string | undefined,
  options: readonly SelectOption[],
  pluralKey: PluralKey,
  t: TFunction<"mlExports">,
): string | null => {
  const list = csvList(value);
  if (list.length === 0) return null;
  if (list.length === 1) return optionLabel(options, list[0]);
  return t("filterBar.multi", {
    value: formatNumber(list.length),
    plural: t(pluralKey),
  });
};

/* ──────────────────────────────────────────────────────────────────
 * Option sets
 * ────────────────────────────────────────────────────────────────── */

/** Option `value`s are wire values; only the display labels are translated. */
const reviewStateOptions = (t: TFunction<"mlExports">): SelectOption[] => [
  { value: "MODEL_ONLY", label: t("filterOptions.reviewState.modelOnly") },
  { value: "MODEL_CORRECTED", label: t("filterOptions.reviewState.corrected") },
  { value: "HUMAN_ADDED", label: t("filterOptions.reviewState.humanAdded") },
  { value: "REVIEW_OUTDATED", label: t("filterOptions.reviewState.outdated") },
];

/** "Human changes" preset = corrections + manually added labels (OR). */
const HUMAN_CHANGES_STATES = ["MODEL_CORRECTED", "HUMAN_ADDED"];

const eventStateOptions = (t: TFunction<"mlExports">): SelectOption[] => [
  { value: "dump", label: t("filterOptions.eventState.dump") },
  { value: "dig", label: t("filterOptions.eventState.dig") },
  { value: "swing-empty", label: t("filterOptions.eventState.swingEmpty") },
  { value: "swing-loaded", label: t("filterOptions.eventState.swingLoaded") },
  { value: "dump-no-truck", label: t("filterOptions.eventState.dumpNoTruck") },
  { value: "boulder-handling", label: t("filterOptions.eventState.boulderHandling") },
  { value: "truck-empty", label: t("filterOptions.eventState.truckEmpty") },
  { value: "truck-loaded", label: t("filterOptions.eventState.truckLoaded") },
  { value: "truck-cycle", label: t("filterOptions.eventState.truckCycle") },
  { value: "unproductive", label: t("filterOptions.eventState.unproductive") },
  { value: "abnormal", label: t("filterOptions.eventState.abnormal") },
];

const presenceOptions = (t: TFunction<"mlExports">): SelectOption[] => [
  { value: "", label: t("filterOptions.presence.any") },
  { value: "unreviewed", label: t("filterOptions.presence.unreviewed") },
  { value: "reviewed", label: t("filterOptions.presence.reviewed") },
];

const validityOptions = (t: TFunction<"mlExports">): SelectOption[] => [
  { value: "valid", label: t("filterOptions.validity.valid") },
  { value: "invalid", label: t("filterOptions.validity.invalid") },
  { value: "unknown", label: t("filterOptions.validity.unknown") },
];

/**
 * Alert verdicts for `alert_status_in`.
 *
 * Wording matches the alert queue verbatim (Pending/Resolved/Ignored) — the
 * queue has no "Closed" alias, so neither does this. "No alert" is the
 * blind-spot pool: events nobody was ever paged about.
 */
const alertStatusOptions = (
  t: TFunction<"mlExports">,
): { value: AlertStatusFilter; label: string }[] => [
  { value: "Pending", label: t("alertStatus.pending") },
  { value: "Resolved", label: t("alertStatus.resolved") },
  { value: "Ignored", label: t("alertStatus.ignored") },
  { value: "none", label: t("alertStatus.none") },
];

/** Alert types come from the alert queue's own list so the words match. */
const alertTypeOptions = (): SelectOption[] =>
  ALERT_TYPES.map((type) => ({
    value: type,
    label: alertTypeLabel(type),
  }));

type ConfidencePresetKey =
  | "filterOptions.confidence.all"
  | "filterOptions.confidence.low"
  | "filterOptions.confidence.mid"
  | "filterOptions.confidence.high";

const CONFIDENCE_PRESETS: readonly {
  labelKey: ConfidencePresetKey;
  min: number | undefined;
  max: number | undefined;
}[] = [
  { labelKey: "filterOptions.confidence.all", min: undefined, max: undefined },
  { labelKey: "filterOptions.confidence.low", min: undefined, max: 0.5 },
  { labelKey: "filterOptions.confidence.mid", min: 0.5, max: 0.8 },
  { labelKey: "filterOptions.confidence.high", min: 0.8, max: undefined },
];

type TimePresetKey =
  | "filterOptions.timePreset.lastHour"
  | "filterOptions.timePreset.today"
  | "filterOptions.timePreset.last24h"
  | "filterOptions.timePreset.last7Days"
  | "filterOptions.timePreset.last30Days";

const TIME_PRESETS: { labelKey: TimePresetKey; getAfter: () => string }[] = [
  {
    labelKey: "filterOptions.timePreset.lastHour",
    getAfter: () => new Date(Date.now() - 60 * 60 * 1000).toISOString(),
  },
  {
    labelKey: "filterOptions.timePreset.today",
    getAfter: () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.toISOString();
    },
  },
  {
    labelKey: "filterOptions.timePreset.last24h",
    getAfter: () => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    labelKey: "filterOptions.timePreset.last7Days",
    getAfter: () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    labelKey: "filterOptions.timePreset.last30Days",
    getAfter: () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const fmtShort = (iso: string | undefined) =>
  iso ? formatDate(new Date(iso)) : "";

/** Preset label when the applied window is one, else a from → to reading. */
const dateRangeSummary = (
  after: string | undefined,
  before: string | undefined,
  t: TFunction<"mlExports">,
): string | null => {
  if (!after && !before) return null;
  if (after && !before) {
    const appliedMs = new Date(after).getTime();
    for (const p of TIME_PRESETS) {
      // 60s tolerance — the preset is recomputed against "now" every render.
      if (Math.abs(appliedMs - new Date(p.getAfter()).getTime()) < 60_000) {
        return t(p.labelKey);
      }
    }
  }
  return t("filterBar.dateRange", {
    from: fmtShort(after) || t("filterBar.dateOpenStart"),
    to: fmtShort(before) || t("filterBar.dateOpenEnd"),
  });
};

/* ──────────────────────────────────────────────────────────────────
 * label_in ⇄ family selection
 * ────────────────────────────────────────────────────────────────── */

const parseLabelIn = (
  labelIn: string | undefined,
  families: TaxonomyFamily[]
): Map<string, FamilyMemberFilter> => {
  const result = new Map<string, FamilyMemberFilter>();
  if (!labelIn) return result;
  const tokens = new Set(csvList(labelIn));
  for (const family of families) {
    const matched = family.members
      .map((member, index) =>
        tokens.has(member.canonical) ||
        member.aliases.some((a) => tokens.has(a))
          ? index
          : -1,
      )
      .filter((index) => index !== -1);
    if (matched.length === 0) continue;
    if (matched.length === family.members.length) result.set(family.key, "any");
    else result.set(family.key, matched[0]);
  }
  return result;
};

const buildLabelIn = (
  selection: Map<string, FamilyMemberFilter>,
  families: TaxonomyFamily[]
): string | undefined => {
  const tokens: string[] = [];
  for (const family of families) {
    const filter = selection.get(family.key);
    if (filter === undefined) continue;
    if (filter === "any") {
      for (const member of family.members) tokens.push(member.canonical);
    } else if (family.members[filter]) {
      tokens.push(family.members[filter].canonical);
    }
  }
  return tokens.length > 0 ? tokens.join(",") : undefined;
};

/* ──────────────────────────────────────────────────────────────────
 * Refinement registry
 *
 * One entry per user-visible refinement: which request keys it owns
 * (so clearing and per-group counting are exact) and how an applied
 * value reads on its chip.
 * ────────────────────────────────────────────────────────────────── */

interface SummaryContext {
  families: TaxonomyFamily[];
  reviewerName: (id: string) => string;
  t: TFunction<"mlExports">;
}

type RefinementLabelKey =
  | "filterBar.refinement.label"
  | "filterBar.refinement.validity"
  | "filterBar.refinement.confidence"
  | "filterBar.refinement.eventState"
  | "filterBar.refinement.alert"
  | "filterBar.refinement.alertType"
  | "filterBar.refinement.eventTime"
  | "filterBar.refinement.presence"
  | "filterBar.refinement.reviewState"
  | "filterBar.refinement.reviewer"
  | "filterBar.refinement.reviewedTime";

interface RefinementDef {
  key: RevealedFilterKey;
  labelKey: RefinementLabelKey;
  icon: ReactNode;
  fields: readonly FilterField[];
  /** Chip text for the applied value, or null when this refinement is unset. */
  summary: (f: AppliedReviewFilters, ctx: SummaryContext) => string | null;
}

const iconClass = "h-3 w-3";

const LABEL_REFINEMENT: RefinementDef = {
  key: "label",
  labelKey: "filterBar.refinement.label",
  icon: <Tag className={iconClass} />,
  fields: ["label_in"],
  summary: (f, ctx) => {
    if (!f.label_in) return null;
    const selection = parseLabelIn(f.label_in, ctx.families);
    // Taxonomy not loaded yet (or tokens outside it) — still say something true.
    if (selection.size === 0)
      return ctx.t("filterBar.summary.labels", {
        value: formatNumber(csvList(f.label_in).length),
      });
    if (selection.size === 1) {
      const [[key, filter]] = [...selection.entries()];
      const family = ctx.families.find((fam) => fam.key === key);
      if (family) {
        const member =
          filter === "any"
            ? ctx.t("filterBar.any")
            : family.members[filter]?.display_name ?? ctx.t("filterBar.any");
        return ctx.t("filterBar.summary.familyMember", {
          family: family.display_name,
          member,
        });
      }
    }
    return ctx.t("filterBar.summary.families", {
      value: formatNumber(selection.size),
    });
  },
};

const VALIDITY_REFINEMENT: RefinementDef = {
  key: "validity",
  labelKey: "filterBar.refinement.validity",
  icon: <CheckCircle2 className={iconClass} />,
  fields: ["validity_state_in", "require_valid_flag"],
  summary: (f, ctx) =>
    multiSummary(
      f.validity_state_in,
      validityOptions(ctx.t),
      "filterBar.plural.states",
      ctx.t,
    ) ??
    (f.require_valid_flag === true
      ? ctx.t("filterOptions.validity.valid")
      : null),
};

const CONFIDENCE_REFINEMENT: RefinementDef = {
  key: "confidence",
  labelKey: "filterBar.refinement.confidence",
  icon: <Gauge className={iconClass} />,
  fields: ["min_confidence", "max_confidence"],
  summary: (f, ctx) =>
    f.min_confidence == null && f.max_confidence == null
      ? null
      : ctx.t("filterBar.summary.confidenceRange", {
          min: formatNumber(Math.round((f.min_confidence ?? 0) * 100)),
          max: formatNumber(Math.round((f.max_confidence ?? 1) * 100)),
        }),
};

const EVENT_STATE_REFINEMENT: RefinementDef = {
  key: "event_state",
  labelKey: "filterBar.refinement.eventState",
  icon: <Layers className={iconClass} />,
  fields: ["event_state_in"],
  summary: (f, ctx) =>
    multiSummary(
      f.event_state_in,
      eventStateOptions(ctx.t),
      "filterBar.plural.states",
      ctx.t,
    ),
};

const ALERT_STATUS_REFINEMENT: RefinementDef = {
  key: "alert",
  labelKey: "filterBar.refinement.alert",
  icon: <Bell className={iconClass} />,
  fields: ["alert_status_in"],
  summary: (f, ctx) =>
    multiSummary(
      f.alert_status_in,
      alertStatusOptions(ctx.t),
      "filterBar.plural.verdicts",
      ctx.t,
    ),
};

const ALERT_TYPE_REFINEMENT: RefinementDef = {
  key: "alert_type",
  labelKey: "filterBar.refinement.alertType",
  icon: <Siren className={iconClass} />,
  fields: ["alert_type_in"],
  summary: (f, ctx) =>
    multiSummary(
      f.alert_type_in,
      alertTypeOptions(),
      "filterBar.plural.types",
      ctx.t,
    ),
};

const EVENT_TIME_REFINEMENT: RefinementDef = {
  key: "event_time",
  labelKey: "filterBar.refinement.eventTime",
  icon: <Clock className={iconClass} />,
  fields: ["alert_after", "alert_before"],
  summary: (f, ctx) => dateRangeSummary(f.alert_after, f.alert_before, ctx.t),
};

const PRESENCE_REFINEMENT: RefinementDef = {
  key: "presence",
  labelKey: "filterBar.refinement.presence",
  icon: <Eye className={iconClass} />,
  fields: ["review_presence"],
  summary: (f, ctx) =>
    f.review_presence
      ? optionLabel(presenceOptions(ctx.t), f.review_presence)
      : null,
};

const REVIEW_STATE_REFINEMENT: RefinementDef = {
  key: "state",
  labelKey: "filterBar.refinement.reviewState",
  icon: <Flag className={iconClass} />,
  fields: ["review_state_in"],
  summary: (f, ctx) => {
    const list = csvList(f.review_state_in);
    if (list.length === 0) return null;
    const isHumanChanges =
      list.length === HUMAN_CHANGES_STATES.length &&
      HUMAN_CHANGES_STATES.every((s) => list.includes(s));
    if (isHumanChanges) return ctx.t("filterBar.humanChanges");
    return multiSummary(
      f.review_state_in,
      reviewStateOptions(ctx.t),
      "filterBar.plural.states",
      ctx.t,
    );
  },
};

const REVIEWER_REFINEMENT: RefinementDef = {
  key: "reviewer",
  labelKey: "filterBar.refinement.reviewer",
  icon: <User className={iconClass} />,
  fields: ["reviewed_by"],
  summary: (f, ctx) => (f.reviewed_by ? ctx.reviewerName(f.reviewed_by) : null),
};

const REVIEWED_TIME_REFINEMENT: RefinementDef = {
  key: "reviewed_in",
  labelKey: "filterBar.refinement.reviewedTime",
  icon: <Calendar className={iconClass} />,
  fields: ["reviewed_after", "reviewed_before"],
  summary: (f, ctx) =>
    dateRangeSummary(f.reviewed_after, f.reviewed_before, ctx.t),
};

type FilterGroupId = "image" | "event" | "review";

interface FilterGroupDef {
  id: FilterGroupId;
  labelKey: "filterBar.group.image" | "filterBar.group.event" | "filterBar.group.review";
  icon: ReactNode;
  /** Popover width — groups differ enough in content to not share one. */
  width: string;
  refinements: readonly RefinementDef[];
}

const FILTER_GROUPS: readonly FilterGroupDef[] = [
  {
    id: "image",
    labelKey: "filterBar.group.image",
    icon: <ImageIcon className="h-3.5 w-3.5" />,
    width: "w-80",
    refinements: [LABEL_REFINEMENT, VALIDITY_REFINEMENT, CONFIDENCE_REFINEMENT],
  },
  {
    id: "event",
    labelKey: "filterBar.group.event",
    icon: <Layers className="h-3.5 w-3.5" />,
    width: "w-72",
    refinements: [
      EVENT_STATE_REFINEMENT,
      ALERT_STATUS_REFINEMENT,
      ALERT_TYPE_REFINEMENT,
      EVENT_TIME_REFINEMENT,
    ],
  },
  {
    id: "review",
    labelKey: "filterBar.group.review",
    icon: <Flag className="h-3.5 w-3.5" />,
    width: "w-72",
    refinements: [
      PRESENCE_REFINEMENT,
      REVIEW_STATE_REFINEMENT,
      REVIEWER_REFINEMENT,
      REVIEWED_TIME_REFINEMENT,
    ],
  },
];

/** Every request key a group's popover is allowed to write. */
const groupFields = (group: FilterGroupDef): readonly FilterField[] =>
  group.refinements.flatMap((r) => [...r.fields]);

const GROUP_FIELDS: Record<FilterGroupId, readonly FilterField[]> = {
  image: groupFields(FILTER_GROUPS[0]),
  event: groupFields(FILTER_GROUPS[1]),
  review: groupFields(FILTER_GROUPS[2]),
};

/* ──────────────────────────────────────────────────────────────────
 * Scope chip — the only chip with an explicit Apply button.
 *
 * Scope changes are the most destructive action on the page:
 * new dataset, cleared selection, scrolled-through progress lost.
 * The Apply button makes that decision visible and reversible.
 * ────────────────────────────────────────────────────────────────── */

interface ScopeChipProps {
  locations: TLocation[];
  selectedLocationId: number | undefined;
  selectedMachineIds: Set<number>;
  onApplyScope: (locationId: number | undefined, machineIds: Set<number>) => void;
  hasScopeSelected: boolean;
}

const setsEqual = (a: Set<number>, b: Set<number>) => {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
};

const ScopeChip = ({
  locations,
  selectedLocationId,
  selectedMachineIds,
  onApplyScope,
  hasScopeSelected,
}: ScopeChipProps) => {
  const { t } = useTranslation("mlExports");
  const [open, setOpen] = useState(false);

  // Local draft — only touched while popover is open.
  const [draftLocationId, setDraftLocationId] = useState(selectedLocationId);
  const [draftMachineIds, setDraftMachineIds] = useState<Set<number>>(
    new Set(selectedMachineIds)
  );

  // Machines list follows the DRAFT location, not applied — otherwise
  // picking a new location in the popover would still show the old
  // location's machines (or "No machines" if none was applied yet).
  const { data: draftMachinesData, isLoading: machinesLoading } =
    useGetMachinesByLocation(draftLocationId);
  const machines = draftMachinesData?.data ?? [];

  // Whenever the popover opens, rehydrate draft from applied.
  // This gives us "Cancel" for free: close without Apply = draft discarded.
  useEffect(() => {
    if (open) {
      setDraftLocationId(selectedLocationId);
      setDraftMachineIds(new Set(selectedMachineIds));
    }
  }, [open, selectedLocationId, selectedMachineIds]);

  // When the user changes location in draft, machines must clear —
  // machine ids don't carry across locations.
  const handleDraftLocation = (val: string) => {
    const nextLoc = val ? parseInt(val) : undefined;
    setDraftLocationId(nextLoc);
    if (nextLoc !== selectedLocationId) setDraftMachineIds(new Set());
  };

  const toggleDraftMachine = (id: number) =>
    setDraftMachineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllDraftMachines = () =>
    setDraftMachineIds((prev) =>
      prev.size === machines.length ? new Set() : new Set(machines.map((m) => m.id))
    );

  const dirty =
    draftLocationId !== selectedLocationId ||
    !setsEqual(draftMachineIds, selectedMachineIds);

  const canApply = !!draftLocationId;

  const handleApply = () => {
    onApplyScope(draftLocationId, draftMachineIds);
    setOpen(false);
  };

  // Chip label — uses applied values only.
  const location = locations.find((l) => l.id === selectedLocationId);
  const machineSummary =
    selectedMachineIds.size === 0
      ? t("scope.allMachines")
      : t("scope.machineCount", {
          count: selectedMachineIds.size,
          value: formatNumber(selectedMachineIds.size),
        });

  const chipValue = hasScopeSelected
    ? t("scope.chipValue", {
        location: location?.name ?? "?",
        machines: machineSummary,
      })
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center h-7 rounded-full border text-xs font-medium transition-colors",
            hasScopeSelected
              ? open
                ? "bg-primary/15 text-primary border-primary ring-2 ring-primary/20"
                : "bg-primary/10 text-primary border-primary hover:bg-primary/15"
              // Unconfigured state — subtle pulse to show this is the required first step.
              : "bg-primary text-primary-foreground border-primary animate-pulse"
          )}
        >
          <span className="inline-flex items-center gap-1.5 px-3 h-full">
            <MapPin className="h-3 w-3" />
            <span>{t("scope.label")}</span>
            {chipValue ? (
              <>
                <span className="opacity-40">·</span>
                <span className="font-semibold">{chipValue}</span>
              </>
            ) : (
              <span className="opacity-80">{t("scope.notSet")}</span>
            )}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="p-3 space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("scope.location")}
          </div>
          <Select
            value={draftLocationId?.toString() ?? ""}
            onValueChange={handleDraftLocation}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder={t("scope.selectLocation")} />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id.toString()}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {draftLocationId && (
            <>
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("scope.machines")}
                </div>
                {machines.length > 0 && (
                  <button
                    onClick={toggleAllDraftMachines}
                    className="text-[11px] text-primary hover:underline"
                  >
                    {draftMachineIds.size === machines.length
                      ? t("scope.deselectAll")
                      : t("scope.selectAll")}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                {machinesLoading ? (
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" /> {t("scope.loadingMachines")}
                  </span>
                ) : machines.length === 0 ? (
                  <span className="text-xs text-muted-foreground">
                    {t("scope.noMachines")}
                  </span>
                ) : (
                  machines.map((m) => {
                    const isActive = draftMachineIds.has(m.id);
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggleDraftMachine(m.id)}
                        className={cn(
                          "px-2 py-0.5 rounded-md text-[11px] font-medium border transition-colors",
                          isActive
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:border-primary/50"
                        )}
                      >
                        {m.name}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Sticky Apply footer — explicit commit. */}
        <div className="px-3 py-2 border-t bg-muted/30 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            {!draftLocationId
              ? t("scope.pickLocation")
              : draftMachineIds.size === 0
                ? t("scope.allIncluded")
                : t("scope.machinesSelected", {
                    count: draftMachineIds.size,
                    value: formatNumber(draftMachineIds.size),
                  })}
          </span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t("scope.cancel")}
            </Button>
            <Button size="sm" disabled={!canApply || !dirty} onClick={handleApply}>
              {t("scope.apply")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Draft-editing controls — all fully controlled by the group draft,
 * so none of them holds a value that could go stale on reopen.
 * ────────────────────────────────────────────────────────────────── */

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
    {children}
  </div>
);

interface CheckboxListProps {
  options: readonly SelectOption[];
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  /** Two columns for long lists; one for short ones. */
  columns?: 1 | 2;
}

const CheckboxList = ({ options, value, onChange, columns = 1 }: CheckboxListProps) => {
  const selected = new Set(csvList(value));
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(csvValue(next));
  };
  return (
    <div className={cn("grid gap-x-2", columns === 2 ? "grid-cols-2" : "grid-cols-1")}>
      {options.map((o) => (
        <label
          key={o.value}
          className="flex items-center gap-2 text-xs cursor-pointer px-1.5 py-1 rounded hover:bg-accent"
        >
          <Checkbox
            checked={selected.has(o.value)}
            onCheckedChange={() => toggle(o.value)}
          />
          <span className="truncate">{o.label}</span>
        </label>
      ))}
    </div>
  );
};

interface SingleSelectListProps {
  options: readonly SelectOption[];
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  className?: string;
}

const SingleSelectList = ({
  options,
  value,
  onChange,
  className,
}: SingleSelectListProps) => (
  <div className={cn("space-y-0.5", className)}>
    {options.map((o) => (
      <button
        key={o.value || "any"}
        type="button"
        onClick={() => onChange(o.value || undefined)}
        className={cn(
          "w-full text-left text-xs px-2 py-1.5 rounded hover:bg-accent",
          (value ?? "") === o.value && "bg-accent font-medium",
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

interface LabelFamilyPickerProps {
  value: string | undefined;
  taxonomy: ClassificationTaxonomy | null | undefined;
  isLoading: boolean;
  onChange: (next: string | undefined) => void;
}

const LabelFamilyPicker = ({
  value,
  taxonomy,
  isLoading,
  onChange,
}: LabelFamilyPickerProps) => {
  const { t } = useTranslation("mlExports");
  const families = useMemo(() => taxonomy?.families ?? [], [taxonomy]);
  const selection = useMemo(() => parseLabelIn(value, families), [value, families]);

  const setMemberFilter = (
    familyKey: string,
    filter: FamilyMemberFilter | null,
  ) => {
    const next = new Map(selection);
    if (filter === null) next.delete(familyKey);
    else next.set(familyKey, filter);
    onChange(buildLabelIn(next, families));
  };

  if (isLoading || !taxonomy) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> {t("filterBar.loadingTaxonomy")}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {taxonomy.families.map((family) => {
        const current = selection.get(family.key);
        const isEnabled = current !== undefined;
        const options: FamilyMemberFilter[] = [
          "any",
          ...family.members.map((_, index) => index),
        ];
        return (
          <div key={family.key} className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={isEnabled}
              onCheckedChange={(checked) =>
                setMemberFilter(family.key, checked === true ? "any" : null)
              }
            />
            <span className="flex-1 truncate font-medium">{family.display_name}</span>
            <div
              className={cn(
                "flex rounded border overflow-hidden text-[10px]",
                !isEnabled && "opacity-40 pointer-events-none"
              )}
            >
              {options.map((option) => {
                const labelText =
                  option === "any"
                    ? t("filterBar.any")
                    : family.members[option].display_name;
                const isSel = current === option;
                return (
                  <button
                    key={option === "any" ? "any" : family.members[option].canonical}
                    type="button"
                    onClick={() => setMemberFilter(family.key, option)}
                    className={cn(
                      "px-1.5 py-0.5 transition-colors",
                      isSel
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-accent"
                    )}
                  >
                    {labelText}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface ConfidenceControlProps {
  min: number | undefined;
  max: number | undefined;
  onChange: (min: number | undefined, max: number | undefined) => void;
}

/** Percent text ⇄ 0–1 fraction. Empty input = no bound. */
const toPercentText = (v: number | undefined) =>
  v != null ? String(Math.round(v * 100)) : "";

const fromPercentText = (text: string): number | undefined => {
  if (text === "") return undefined;
  const n = Number(text);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(Math.max(n, 0), 100) / 100;
};

const ConfidenceControl = ({ min, max, onChange }: ConfidenceControlProps) => {
  const { t } = useTranslation("mlExports");
  return (
  <div className="space-y-2">
    <div className="flex flex-wrap gap-1">
      {CONFIDENCE_PRESETS.map((preset) => {
        const isActive = min === preset.min && max === preset.max;
        return (
          <button
            key={preset.labelKey}
            type="button"
            onClick={() => onChange(preset.min, preset.max)}
            className={cn(
              "px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors border",
              isActive
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary/50",
            )}
          >
            {t(preset.labelKey)}
          </button>
        );
      })}
    </div>

    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground shrink-0">
        {t("filterBar.custom")}
      </span>
      <input
        type="number"
        min={0}
        max={100}
        step={5}
        placeholder={t("filterBar.min")}
        aria-label={t("filterBar.minConfidence")}
        value={toPercentText(min)}
        onChange={(e) => onChange(fromPercentText(e.target.value), max)}
        className="h-7 w-[56px] text-xs px-1.5 rounded-md border bg-background tabular-nums text-center"
      />
      <span className="text-[11px] text-muted-foreground">–</span>
      <input
        type="number"
        min={0}
        max={100}
        step={5}
        placeholder={t("filterBar.max")}
        aria-label={t("filterBar.maxConfidence")}
        value={toPercentText(max)}
        onChange={(e) => onChange(min, fromPercentText(e.target.value))}
        className="h-7 w-[56px] text-xs px-1.5 rounded-md border bg-background tabular-nums text-center"
      />
      <span className="text-[11px] text-muted-foreground">%</span>
    </div>
  </div>
  );
};

interface DateRangeControlProps {
  after: string | undefined;
  before: string | undefined;
  onChange: (after: string | undefined, before: string | undefined) => void;
}

const DateRangeControl = ({ after, before, onChange }: DateRangeControlProps) => {
  const { t } = useTranslation("mlExports");
  const [showCustom, setShowCustom] = useState(false);
  const activePreset = !before && after ? dateRangeSummary(after, before, t) : null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {TIME_PRESETS.map((preset) => (
          <button
            key={preset.labelKey}
            type="button"
            onClick={() => onChange(preset.getAfter(), undefined)}
            className={cn(
              "px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors",
              activePreset === t(preset.labelKey)
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground",
            )}
          >
            {t(preset.labelKey)}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setShowCustom(!showCustom)}
        className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
      >
        {showCustom ? t("filterBar.hideCustomRange") : t("filterBar.customRange")}
      </button>

      {showCustom && (
        <div className="space-y-2 pt-1 border-t border-border/50">
          <label className="block text-xs">
            {t("filterBar.from")}
            <input
              type="datetime-local"
              value={after?.slice(0, 16) ?? ""}
              onChange={(e) =>
                onChange(
                  e.target.value ? new Date(e.target.value).toISOString() : undefined,
                  before,
                )
              }
              className="mt-1 w-full h-8 text-xs px-2 rounded-md border bg-background"
            />
          </label>
          <label className="block text-xs">
            {t("filterBar.to")}
            <input
              type="datetime-local"
              value={before?.slice(0, 16) ?? ""}
              onChange={(e) =>
                onChange(
                  after,
                  e.target.value ? new Date(e.target.value).toISOString() : undefined,
                )
              }
              className="mt-1 w-full h-8 text-xs px-2 rounded-md border bg-background"
            />
          </label>
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Group popover — the ONE place that holds a draft and commits it
 * ────────────────────────────────────────────────────────────────── */

/** Copy just this group's request keys out of the applied filters. */
const sliceFields = (
  applied: AppliedReviewFilters,
  fields: readonly FilterField[],
): AppliedReviewFilters => {
  const draft: AppliedReviewFilters = {};
  for (const field of fields) {
    // Widening through unknown keeps this one assignment honest instead of a
    // cast per key; `fields` is what makes it safe.
    (draft as Record<string, unknown>)[field] = applied[field];
  }
  return draft;
};

/** Only the keys the user actually changed — an untouched group commits nothing. */
const buildPatch = (
  draft: AppliedReviewFilters,
  applied: AppliedReviewFilters,
  fields: readonly FilterField[],
): FilterPatch => {
  const patch: FilterPatch = {};
  for (const field of fields) {
    if (draft[field] === applied[field]) continue;
    (patch as Record<string, unknown>)[field] = draft[field];
  }
  return patch;
};

interface FilterGroupProps {
  group: FilterGroupDef;
  applied: AppliedReviewFilters;
  count: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommit: (patch: FilterPatch) => void;
  children: (
    draft: AppliedReviewFilters,
    patch: (next: FilterPatch) => void,
  ) => ReactNode;
}

const FilterGroup = ({
  group,
  applied,
  count,
  open,
  onOpenChange,
  onCommit,
  children,
}: FilterGroupProps) => {
  const { t } = useTranslation("mlExports");
  const fields = GROUP_FIELDS[group.id];
  const [draft, setDraft] = useState<AppliedReviewFilters>(() =>
    sliceFields(applied, fields),
  );

  // Rehydrate on open — closing without touching anything is a no-op, and
  // reopening never shows a stale draft from a previous visit.
  useEffect(() => {
    if (open) setDraft(sliceFields(applied, fields));
  }, [open, applied, fields]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const patch = buildPatch(draft, applied, fields);
      // Commit on close — ONE request for however many controls were touched.
      if (Object.keys(patch).length > 0) onCommit(patch);
    }
    onOpenChange(next);
  };

  const patch = (next: FilterPatch) =>
    setDraft((prev) => ({ ...prev, ...next }));

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-xs font-medium transition-colors",
            count > 0
              ? open
                ? "bg-primary/15 text-primary border-primary ring-2 ring-primary/20"
                : "bg-primary/10 text-primary border-primary hover:bg-primary/15"
              : open
                ? "bg-muted/60 text-foreground border-border ring-2 ring-primary/20"
                : "bg-muted/40 text-muted-foreground border-border hover:bg-muted/60",
          )}
        >
          {group.icon}
          <span>{t(group.labelKey)}</span>
          {count > 0 && (
            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold tabular-nums">
              {formatNumber(count)}
            </span>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("p-3 space-y-3 max-h-[70vh] overflow-y-auto", group.width)}
        align="start"
      >
        {children(draft, patch)}
        <p className="text-[10px] text-muted-foreground pt-1 border-t">
          {t("filterBar.commitHint")}
        </p>
      </PopoverContent>
    </Popover>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Applied-refinement chip — read-out + removal, not an editor.
 * Clicking it reopens the group that owns it.
 * ────────────────────────────────────────────────────────────────── */

interface RefinementChipProps {
  icon: ReactNode;
  label: string;
  value: string;
  onOpen: () => void;
  onRemove: () => void;
}

const RefinementChip = ({
  icon,
  label,
  value,
  onOpen,
  onRemove,
}: RefinementChipProps) => {
  const { t } = useTranslation("mlExports");
  return (
  <div className="inline-flex items-center h-7 rounded-full border text-xs font-medium transition-colors bg-primary/10 text-primary border-primary hover:bg-primary/15">
    <button
      type="button"
      onClick={onOpen}
      className="inline-flex items-center gap-1.5 px-3 h-full"
    >
      {icon}
      <span>{label}</span>
      <span className="opacity-40">·</span>
      <span className="font-semibold">{value}</span>
    </button>
    <button
      type="button"
      onClick={onRemove}
      className="pr-2 pl-0 hover:text-destructive"
      aria-label={t("filterBar.clearFilter", { label })}
    >
      <X className="h-3 w-3" />
    </button>
  </div>
  );
};

/* ──────────────────────────────────────────────────────────────────
 * Orchestrator
 * ────────────────────────────────────────────────────────────────── */

const ReviewFilterBar = ({
  appliedFilters,
  onApplyFilters,
  onClearFilter,
  onClearAll,
  hasRefinement,
  locations,
  selectedLocationId,
  selectedMachineIds,
  onApplyScope,
  hasScopeSelected,
  presets,
  history,
  onSavePreset,
  onDeletePreset,
  onApplySnapshot,
}: ReviewFilterBarProps) => {
  const { t } = useTranslation("mlExports");
  // At most one group is open at a time, so an applied chip can reopen the
  // group that owns it.
  const [openGroup, setOpenGroup] = useState<FilterGroupId | null>(null);

  const { data: taxonomy, isLoading: taxonomyLoading } =
    useGetClassificationTaxonomy();
  const { selectedOrganizationId } = useOrganizationStore();
  const { data: members } = useGetOrganizationMembers(selectedOrganizationId);

  const summaryContext = useMemo<SummaryContext>(
    () => ({
      families: taxonomy?.families ?? [],
      reviewerName: (id) => {
        const member = members?.find((m) => String(m.id) === id);
        return member?.full_name || member?.username || id;
      },
      t,
    }),
    [taxonomy, members, t],
  );

  const reviewerOptions = useMemo<SelectOption[]>(
    () => [
      { value: "", label: t("filterBar.anyReviewer") },
      ...(members ?? []).map((m) => ({
        value: String(m.id),
        label: m.full_name || m.username,
      })),
    ],
    [members, t],
  );

  const validityChoices = useMemo(() => validityOptions(t), [t]);
  const eventStateChoices = useMemo(() => eventStateOptions(t), [t]);
  const alertStatusChoices = useMemo(() => alertStatusOptions(t), [t]);
  const alertTypeChoices = useMemo(() => alertTypeOptions(), []);
  const presenceChoices = useMemo(() => presenceOptions(t), [t]);
  const reviewStateChoices = useMemo(() => reviewStateOptions(t), [t]);

  // Everything currently refined, in group order, with its chip reading.
  const activeRefinements = useMemo(
    () =>
      FILTER_GROUPS.flatMap((group) =>
        group.refinements
          .map((refinement) => ({
            group,
            refinement,
            value: refinement.summary(appliedFilters, summaryContext),
          }))
          .filter(
            (entry): entry is typeof entry & { value: string } => entry.value != null,
          ),
      ),
    [appliedFilters, summaryContext],
  );

  const countFor = (groupId: FilterGroupId) =>
    activeRefinements.filter((r) => r.group.id === groupId).length;

  const commit = (patch: FilterPatch) => onApplyFilters(patch);

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Scope — always first, always visible, required. */}
      <ScopeChip
        locations={locations}
        selectedLocationId={selectedLocationId}
        selectedMachineIds={selectedMachineIds}
        onApplyScope={onApplyScope}
        hasScopeSelected={hasScopeSelected}
      />

      {/* Refinement groups + the chips reading back what they applied. Until
          a scope is chosen they're muted + non-interactive, preserving the
          guarantee that no refinement request fires before a valid scope. */}
      <div
        className={cn(
          "flex items-center gap-1.5 flex-wrap",
          !hasScopeSelected && "opacity-40 pointer-events-none",
        )}
        aria-disabled={!hasScopeSelected}
      >
        <FilterGroup
          group={FILTER_GROUPS[0]}
          applied={appliedFilters}
          count={countFor("image")}
          open={openGroup === "image"}
          onOpenChange={(open) => setOpenGroup(open ? "image" : null)}
          onCommit={commit}
        >
          {(draft, patch) => (
            <>
              <SectionHeading>{t("filterBar.section.label")}</SectionHeading>
              <LabelFamilyPicker
                value={draft.label_in}
                taxonomy={taxonomy}
                isLoading={taxonomyLoading}
                onChange={(label_in) => patch({ label_in })}
              />

              <SectionHeading>{t("filterBar.section.validity")}</SectionHeading>
              <CheckboxList
                options={validityChoices}
                value={draft.validity_state_in}
                onChange={(validity_state_in) =>
                  // Editing the tri-state supersedes the legacy boolean shortcut.
                  patch({ validity_state_in, require_valid_flag: undefined })
                }
              />

              <SectionHeading>{t("filterBar.section.confidence")}</SectionHeading>
              <ConfidenceControl
                min={draft.min_confidence}
                max={draft.max_confidence}
                onChange={(min_confidence, max_confidence) =>
                  patch({ min_confidence, max_confidence })
                }
              />
            </>
          )}
        </FilterGroup>

        <FilterGroup
          group={FILTER_GROUPS[1]}
          applied={appliedFilters}
          count={countFor("event")}
          open={openGroup === "event"}
          onOpenChange={(open) => setOpenGroup(open ? "event" : null)}
          onCommit={commit}
        >
          {(draft, patch) => (
            <>
              <SectionHeading>{t("filterBar.section.eventState")}</SectionHeading>
              <CheckboxList
                options={eventStateChoices}
                value={draft.event_state_in}
                columns={2}
                onChange={(event_state_in) => patch({ event_state_in })}
              />

              <SectionHeading>{t("filterBar.section.alertStatus")}</SectionHeading>
              <CheckboxList
                options={alertStatusChoices}
                value={draft.alert_status_in}
                columns={2}
                onChange={(alert_status_in) => patch({ alert_status_in })}
              />

              <SectionHeading>{t("filterBar.section.alertType")}</SectionHeading>
              <CheckboxList
                options={alertTypeChoices}
                value={draft.alert_type_in}
                onChange={(alert_type_in) => patch({ alert_type_in })}
              />

              <SectionHeading>{t("filterBar.section.eventTime")}</SectionHeading>
              <DateRangeControl
                after={draft.alert_after}
                before={draft.alert_before}
                onChange={(alert_after, alert_before) =>
                  patch({ alert_after, alert_before })
                }
              />
            </>
          )}
        </FilterGroup>

        <FilterGroup
          group={FILTER_GROUPS[2]}
          applied={appliedFilters}
          count={countFor("review")}
          open={openGroup === "review"}
          onOpenChange={(open) => setOpenGroup(open ? "review" : null)}
          onCommit={commit}
        >
          {(draft, patch) => (
            <>
              <SectionHeading>{t("filterBar.section.presence")}</SectionHeading>
              <SingleSelectList
                options={presenceChoices}
                value={draft.review_presence}
                onChange={(value) =>
                  patch({ review_presence: value as ReviewPresence | undefined })
                }
              />

              <SectionHeading>{t("filterBar.section.reviewState")}</SectionHeading>
              <button
                type="button"
                onClick={() =>
                  patch({ review_state_in: HUMAN_CHANGES_STATES.join(",") })
                }
                className="w-full text-left text-[11px] font-medium px-2 py-1.5 rounded text-primary hover:bg-primary/10"
              >
                {t("filterBar.humanChangesAction")}
              </button>
              <CheckboxList
                options={reviewStateChoices}
                value={draft.review_state_in}
                onChange={(review_state_in) => patch({ review_state_in })}
              />

              <SectionHeading>{t("filterBar.section.reviewer")}</SectionHeading>
              <SingleSelectList
                options={reviewerOptions}
                value={draft.reviewed_by}
                onChange={(reviewed_by) => patch({ reviewed_by })}
                className="max-h-40 overflow-y-auto"
              />

              <SectionHeading>{t("filterBar.section.reviewedTime")}</SectionHeading>
              <DateRangeControl
                after={draft.reviewed_after}
                before={draft.reviewed_before}
                onChange={(reviewed_after, reviewed_before) =>
                  patch({ reviewed_after, reviewed_before })
                }
              />
            </>
          )}
        </FilterGroup>

        {activeRefinements.map(({ group, refinement, value }) => (
          <RefinementChip
            key={refinement.key}
            icon={refinement.icon}
            label={t(refinement.labelKey)}
            value={value}
            onOpen={() => setOpenGroup(group.id)}
            onRemove={() => onClearFilter(refinement.key)}
          />
        ))}
      </div>

      {hasRefinement && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={onClearAll}
          title={t("filterBar.clearAll")}
        >
          <RotateCcw className="h-3 w-3 mr-1" />
          {t("filterBar.reset")}
        </Button>
      )}

      {/* Presets — save / apply / delete + recent history. Right-aligned. */}
      <div className="ml-auto">
        <ReviewPresetsMenu
          presets={presets}
          history={history}
          onSave={onSavePreset}
          onDelete={onDeletePreset}
          onApply={onApplySnapshot}
        />
      </div>
    </div>
  );
};

export default ReviewFilterBar;
